import React, { forwardRef } from 'react';

import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import type { AlineButtonProps } from 'src/components/generic/AlineButton';
import { AlineButton } from 'src/components/generic/AlineButton';

export type IntegrationActionButtonProps = Omit<AlineButtonProps, 'size'> & {
  /** Draws the button as a grey pill, for an integration that is not available yet. */
  muted?: boolean;
  /**
   * When true (the default), the button stays hidden on hover-capable pointers
   * until the row is hovered or focused. Turn off for a CTA the user must
   * notice without hunting, such as reauthorizing Slack.
   */
  revealOnHover?: boolean;
};

export const INTEGRATION_ROW_ACTION_CLASS = 'integrationRowAction';

export const integrationHoverActionRevealStyles = {
  [`& .${INTEGRATION_ROW_ACTION_CLASS}`]: {
    '@media (hover: hover)': {
      opacity: 0,
    },
    transition: 'opacity 120ms ease',
  },
  [`&:focus-within .${INTEGRATION_ROW_ACTION_CLASS}`]: {
    opacity: 1,
  },
  [`&:hover .${INTEGRATION_ROW_ACTION_CLASS}`]: {
    opacity: 1,
  },
};

const IntegrationActionButtonRoot = styled(AlineButton, {
  name: 'IntegrationActionButton',
  shouldForwardProp: prop => prop !== 'muted',
  slot: 'root',
})<{ muted?: boolean }>(({ muted, theme }) => ({
  '& .MuiButton-endIcon': {
    color: 'inherit',
    margin: theme.spacing(0, -0.5, 0, 0.5),
  },
  '& .MuiButton-root': {
    '&:focus-visible': {
      outline: `2px solid ${theme.appColors.halfPrimary}`,
      outlineOffset: 1,
    },
    alignItems: 'center',
    borderRadius: 28,
    display: 'flex',
    justifyContent: 'center',
    lineHeight: 'normal',
    minWidth: 'auto',
    padding: theme.spacing(1, 2),
    width: 'auto',
    ...(muted && {
      '&, &:hover, &.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
        border: `1px solid ${theme.palette.grey[300]}`,
        color: theme.palette.grey[500],
        opacity: 1,
      },
    }),
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
    margin: theme.spacing(0, 0.5, 0, -0.5),
  },
}));

export const IntegrationActionButton = forwardRef(function IntegrationActionButton(
  {
    className,
    disableElevation = true,
    revealOnHover = true,
    variant = 'outlined',
    ...props
  }: IntegrationActionButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  return (
    <IntegrationActionButtonRoot
      ref={ref}
      className={clsx(revealOnHover && INTEGRATION_ROW_ACTION_CLASS, className)}
      color="primary"
      disableElevation={disableElevation}
      variant={variant}
      {...props}
    />
  );
});
